/**
 * Motion for product pages. Loaded only when the product section's "Motion"
 * setting is on (see sections/product-information.liquid), after GSAP core.
 *
 * - Nothing that is on screen when the page loads is hidden. The first photo, title
 *   and price settle in with CSS instead, so they animate from the first frame.
 * - Scroll reveals use IntersectionObserver rather than ScrollTrigger, and only
 *   prepare elements that are still below the fold.
 * - Only transform and opacity are animated, and nothing runs for people whose
 *   device asks for reduced motion. Tweens clear their inline styles when done.
 * - Press feedback is CSS (in the section), building on the theme's own :active
 *   styles. The swatch pop and cart bump use element.animate(), which leaves no
 *   inline styles that would override the theme's hover and press transforms.
 * - Handlers are delegated or attached outside the product section, so they keep
 *   working after the pack switcher swaps that section in place.
 */
(() => {
  const { gsap } = window;
  if (!gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const EASE = 'power2.out';

  /** Run one piece of motion; if it fails, the rest still run. */
  const safely = (setup) => {
    try {
      setup();
    } catch (error) {
      console.error('[product-motion]', error);
    }
  };

  /** @param {Element} el */
  const belowFold = (el) => el.getBoundingClientRect().top > window.innerHeight;

  /**
   * Call `play` with the targets that have just scrolled into view, once each.
   * @param {Element[]} targets
   * @param {(entering: Element[]) => void} play
   */
  const onReveal = (targets, play) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entering = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);
        if (!entering.length) return;
        entering.forEach((el) => observer.unobserve(el));
        play(entering);
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    targets.forEach((el) => observer.observe(el));
  };

  // Section titles: Features, Customer reviews, FAQ.
  safely(() => {
    const titles = [
      document.querySelector('[class*="ai-features-section-"]')?.closest('.shopify-section')?.querySelector('.text-block'),
      document.querySelector('.combined-reviews__heading'),
      document.querySelector('.shopify-section[id*="faq_section"] .text-block'),
    ].filter((el) => el && belowFold(el));
    if (!titles.length) return;
    gsap.set(titles, { opacity: 0, y: 16 });
    onReveal(titles, (entering) =>
      gsap.to(entering, { opacity: 1, y: 0, duration: 0.6, ease: EASE, clearProps: 'opacity,transform' })
    );
  });

  // Feature cards fade up, a row at a time as they come into view.
  safely(() => {
    const cards = [...document.querySelectorAll('[class*="ai-feature-card-"]')].filter(belowFold);
    if (!cards.length) return;
    gsap.set(cards, { opacity: 0, y: 24 });
    onReveal(cards, (entering) =>
      gsap.to(entering, { opacity: 1, y: 0, duration: 0.6, ease: EASE, stagger: 0.08, clearProps: 'opacity,transform' })
    );
  });

  // Reviews: the score and count count up, the stars fill, the bars grow, then the
  // marquee fades in.
  safely(() => {
    const section = document.querySelector('.combined-reviews');
    const average = section?.querySelector('.combined-reviews__average');
    if (!section || !average || !belowFold(average)) return;

    const stars = section.querySelector('.combined-reviews__score .review-card__stars');
    const count = section.querySelector('.combined-reviews__count');
    const bars = section.querySelectorAll('.combined-reviews__bar-track > span');
    const marquee = section.querySelector('.combined-reviews__marquee');

    const averageEnd = parseFloat(average.textContent) || 0;
    const countText = count?.textContent ?? '';
    const countEnd = parseInt(countText.replace(/\D+/g, ''), 10) || 0;
    const rating = stars ? getComputedStyle(stars).getPropertyValue('--rating').trim() || String(averageEnd) : '';

    const show = (value) => {
      average.textContent = value.toFixed(2);
      if (count && countEnd) count.textContent = countText.replace(/\d+/, String(Math.round((value / averageEnd) * countEnd)));
    };

    show(0);
    if (stars) gsap.set(stars, { '--rating': 0 });
    if (bars.length) gsap.set(bars, { scaleX: 0, transformOrigin: 'left center' });
    if (marquee) gsap.set(marquee, { opacity: 0, y: 20 });

    onReveal([average], () => {
      const counter = { value: 0 };
      const timeline = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onComplete: () => {
          show(averageEnd);
          if (count) count.textContent = countText;
          gsap.set([...bars, marquee].filter(Boolean), { clearProps: 'opacity,transform' });
        },
      });
      timeline.to(counter, { value: averageEnd, duration: 1.2, onUpdate: () => show(counter.value) }, 0);
      if (stars) timeline.to(stars, { '--rating': rating, duration: 1.2 }, 0);
      if (bars.length) timeline.to(bars, { scaleX: 1, duration: 0.9, stagger: 0.06 }, 0.1);
      if (marquee) timeline.to(marquee, { opacity: 1, y: 0, duration: 0.7, ease: EASE }, 0.35);
    });
  });

  // Build-your-own: the picked swatch pops.
  safely(() => {
    document.addEventListener('click', (event) => {
      const swatch = event.target instanceof Element ? event.target.closest('.combo-builder__swatch') : null;
      if (!swatch || swatch.dataset.unavailable === 'true') return;
      swatch.animate([{ transform: 'scale(1.15)' }, { transform: 'scale(1)' }], {
        duration: 450,
        easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      });
    });
  });

  // When the price, the waist note or a picked colour name changes, the new text
  // slides in. Watches text only, inside the product section.
  safely(() => {
    const section = document.querySelector('.product-details')?.closest('.shopify-section');
    if (!section) return;
    const WATCHED = '.product-details [ref="priceContainer"], [data-size-note-output], [data-picked-name]';
    /** @type {WeakMap<Element, string>} */
    const lastText = new WeakMap();
    section.querySelectorAll(WATCHED).forEach((el) => lastText.set(el, el.textContent ?? ''));

    new MutationObserver((mutations) => {
      const changed = new Set();
      for (const mutation of mutations) {
        const node = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
        const el = node?.closest(WATCHED);
        if (el) changed.add(el);
      }
      for (const el of changed) {
        const text = el.textContent ?? '';
        if (lastText.get(el) === text) continue;
        lastText.set(el, text);
        gsap.fromTo(el, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.35, ease: EASE, clearProps: 'opacity,transform' });
      }
    }).observe(section, { childList: true, characterData: true, subtree: true });
  });

  // The header cart icon gives a small bounce when something lands in the cart.
  safely(() => {
    document.addEventListener('cart:update', () => {
      const icon = document.querySelector('.header-actions__cart-icon');
      if (!icon) return;
      icon.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.2)' }, { transform: 'scale(1)' }], {
        duration: 360,
        easing: 'ease-out',
      });
    });
  });
})();
